import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { mediaUrl, formatApiError } from "@/lib/api";
import { Heart, Eye, ArrowLeft, Send } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function VideoDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [video, setVideo] = useState(null);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const [v, c] = await Promise.all([
          api.get(`/videos/${id}`),
          api.get(`/videos/${id}/comments`),
        ]);
        setVideo(v.data);
        setComments(c.data);
        setLiked(v.data.likes?.includes(user?.id));
        setLikeCount(v.data.likes?.length || 0);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, [id, user?.id]);

  const toggleLike = async () => {
    try {
      const { data } = await api.post(`/videos/${id}/like`);
      setLiked(data.liked);
      setLikeCount(data.like_count);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendComment = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      const { data } = await api.post(`/videos/${id}/comments`, { text });
      setComments([data, ...comments]);
      setText("");
    } catch (e2) { toast.error(formatApiError(e2)); }
  };

  if (!video) return <div className="p-10 text-[#a3a3a3] text-sm">Loading…</div>;

  return (
    <div className="p-6 sm:p-10 max-w-5xl mx-auto">
      <Link to="/app/videos" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#a3a3a3] hover:text-[#ff4500]" data-testid="back-to-videos">
        <ArrowLeft className="w-3 h-3" /> All videos
      </Link>

      <div className="mt-6 relative aspect-video bg-black border border-[#2a2a2a]">
        <video
          src={mediaUrl(video.video_url)}
          controls
          autoPlay
          className="w-full h-full"
          data-testid="video-player-el"
        />
      </div>

      <div className="mt-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tighter">{video.title}</h1>
          <div className="text-sm text-[#a3a3a3] mt-2 flex items-center gap-4">
            <span>{video.owner_name}</span>
            <span className="flex items-center gap-1 font-mono text-xs"><Eye className="w-3.5 h-3.5" />{video.views}</span>
          </div>
        </div>
        <button
          onClick={toggleLike}
          data-testid="like-video-btn"
          className={`inline-flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-[0.2em] border transition-colors ${
            liked
              ? "bg-[#ff4500] text-white border-[#ff4500]"
              : "bg-[#141414] border-[#2a2a2a] hover:border-[#ff4500] text-[#f5f5f5]"
          }`}
        >
          <Heart className={`w-4 h-4 ${liked ? "fill-current" : ""}`} /> {likeCount}
        </button>
      </div>

      {video.description && (
        <p className="text-[#a3a3a3] mt-6 text-sm leading-relaxed whitespace-pre-line bg-[#141414] border border-[#2a2a2a] p-4">
          {video.description}
        </p>
      )}

      {/* Comments */}
      <section className="mt-10">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#ff4500] font-mono mb-4">── {comments.length} comments</div>
        <form onSubmit={sendComment} className="flex gap-2 mb-6" data-testid="comment-form">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Add a comment…"
            data-testid="comment-input"
            className="flex-1 bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#ff4500] outline-none px-4 py-2.5 text-sm"
          />
          <button type="submit" data-testid="send-comment-btn" className="px-4 py-2.5 bg-[#ff4500] hover:bg-[#ff5722] text-white">
            <Send className="w-4 h-4" />
          </button>
        </form>
        <div className="space-y-4">
          {comments.map((c) => (
            <div key={c.id} className="flex gap-3" data-testid={`comment-${c.id}`}>
              <div className="w-9 h-9 bg-[#1a1a1a] border border-[#2a2a2a] flex items-center justify-center shrink-0">
                {c.user_avatar ? (
                  <img src={mediaUrl(c.user_avatar)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-xs text-[#ff4500] font-display">{c.user_name?.[0]?.toUpperCase()}</span>
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{c.user_name}</span>
                  <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-[#a3a3a3]">
                    {new Date(c.created_at).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-sm text-[#a3a3a3] mt-1">{c.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
